import React, { useState, useEffect } from 'react';
import { 
  ClockCircleOutlined, 
  CloseCircleOutlined, 
  WalletOutlined, 
  NotificationOutlined,
  FileTextOutlined,
  RightOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { apiClient, API_ENDPOINTS } from '../../utils/api';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  red: '#F87171',
  orange: '#F59E0B',
  purple: '#C084FC',
};

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.7);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(248, 113, 113, 0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const ActionItemsContainer = styled.div`
  margin-bottom: 32px;
  animation: ${slideIn} 0.5s ease-out;
`;

const ActionItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActionItem = styled.div<{ hasCount: boolean }>`
  background: linear-gradient(135deg, ${COLORS.bgSecondary} 0%, #1a1f28 100%);
  border: 1px solid ${props => props.hasCount ? COLORS.red : COLORS.border};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${COLORS.red} 0%, ${COLORS.orange} 50%, ${COLORS.purple} 100%);
    opacity: ${props => props.hasCount ? 1 : 0};
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    border-color: ${props => props.hasCount ? COLORS.red : COLORS.accent};
    box-shadow: ${props => props.hasCount 
      ? '0 8px 24px rgba(248, 113, 113, 0.3)' 
      : '0 8px 24px rgba(34, 211, 238, 0.2)'};
    
    .action-icon {
      transform: scale(1.1) rotate(5deg);
    }
    
    .action-arrow {
      transform: translateX(4px);
    }
  }
  
  ${props => props.hasCount && css`
    animation: ${pulse} 2s infinite;
  `}
`;

const ActionItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const ActionIconWrapper = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${props => props.color}20 0%, ${props => props.color}10 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.color}40;
  
  .action-icon {
    font-size: 24px;
    color: ${props => props.color};
    transition: all 0.3s ease;
  }
`;

const ActionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.textPrimary};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionDescription = styled.div`
  font-size: 12px;
  color: ${COLORS.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionBadge = styled.div<{ hasCount: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
`;

const BadgeCount = styled.div<{ hasCount: boolean }>`
  min-width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.hasCount 
    ? 'linear-gradient(135deg, #F87171 0%, #EF4444 100%)' 
    : 'linear-gradient(135deg, #1f2a37 0%, #161B22 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.hasCount ? '#FFFFFF' : COLORS.textSecondary};
  border: ${props => props.hasCount ? '2px solid #F87171' : `2px solid ${COLORS.border}`};
  box-shadow: ${props => props.hasCount 
    ? '0 4px 12px rgba(248, 113, 113, 0.4)' 
    : 'none'};
  transition: all 0.3s ease;
`;

const ActionArrow = styled.div`
  color: ${COLORS.accent};
  font-size: 16px;
  transition: all 0.3s ease;
  margin-left: 8px;
  
  .action-arrow {
    transition: all 0.3s ease;
  }
`;

interface ActionItemData {
  key: string;
  label: string;
  description: string;
  count: number;
  icon: React.ReactNode;
  color: string;
  link: string;
}

interface ActionItemsProps {
  isAdmin?: boolean;
}

const ActionItems: React.FC<ActionItemsProps> = ({ isAdmin = true }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionItems, setActionItems] = useState<ActionItemData[]>([]);

  const fetchActionItems = React.useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = isAdmin
        ? API_ENDPOINTS.ADMIN_GET_ACTION_ITEMS
        : API_ENDPOINTS.MERCHANT_GET_ACTION_ITEMS;
      
      const response = await apiClient.get(endpoint);
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        
        const items: ActionItemData[] = [
          {
            key: 'pendingSweeps',
            label: 'Pending Sweeps',
            description: 'Sweeps awaiting processing',
            count: data.pendingSweeps || 0,
            icon: <ClockCircleOutlined />,
            color: COLORS.orange,
            link: isAdmin ? '/kivu/sweep-list?status=PENDING' : '/igwena/sweep-list?status=PENDING',
          },
          {
            key: 'failedSweeps',
            label: 'Failed Sweeps',
            description: 'Sweeps that need attention',
            count: data.failedSweeps || 0,
            icon: <CloseCircleOutlined />,
            color: COLORS.red,
            link: isAdmin ? '/kivu/sweep-list?status=FAILED' : '/igwena/sweep-list?status=FAILED',
          },
          {
            key: 'failedWebhooks',
            label: 'Failed Webhooks',
            description: 'Webhooks that need retry',
            count: data.failedWebhooks || 0,
            icon: <NotificationOutlined />,
            color: COLORS.red,
            link: isAdmin ? '/kivu/webhooks?status=FAILED' : '/igwena/webhooks?status=FAILED',
          },
        ];

        // Only show unassigned wallets and update requests for admin
        if (isAdmin) {
          items.push({
            key: 'unassignedWallets',
            label: 'Unassigned Wallets',
            description: 'Wallets ready for assignment',
            count: data.unassignedWallets || 0,
            icon: <WalletOutlined />,
            color: COLORS.accent,
            link: '/kivu/wallets',
          });
          items.push({
            key: 'pendingUpdateRequests',
            label: 'Merchant Requests',
            description: 'Requests pending review',
            count: data.pendingUpdateRequests || 0,
            icon: <FileTextOutlined />,
            color: COLORS.purple,
            link: '/kivu/merchant-update-requests?status=PENDING',
          });
          items.push({
            key: 'pendingInvoices',
            label: 'Pending Invoices',
            description: 'Invoices awaiting payment',
            count: data.pendingInvoices || 0,
            icon: <DollarOutlined />,
            color: COLORS.orange,
            link: '/kivu/invoices',
          });
          if (data.overdueInvoices > 0) {
            items.push({
              key: 'overdueInvoices',
              label: 'Overdue Invoices',
              description: 'Invoices past due date',
              count: data.overdueInvoices || 0,
              icon: <ExclamationCircleOutlined />,
              color: COLORS.red,
              link: '/kivu/invoices',
            });
          }
        }

        setActionItems(items.filter(item => item.count > 0));
      }
    } catch (error: any) {
      console.error('Error fetching action items:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchActionItems();
  }, [fetchActionItems]);

  if (loading) {
    return null;
  }

  if (actionItems.length === 0) {
    return null;
  }

  return (
    <ActionItemsContainer>
      <ActionItemsGrid>
        {actionItems.map((item) => (
          <ActionItem
            key={item.key}
            hasCount={item.count > 0}
            onClick={() => navigate(item.link)}
          >
            <ActionItemLeft>
              <ActionIconWrapper color={item.color}>
                <span className="action-icon">{item.icon}</span>
              </ActionIconWrapper>
              <ActionContent>
                <ActionLabel>{item.label}</ActionLabel>
                <ActionDescription>{item.description}</ActionDescription>
              </ActionContent>
            </ActionItemLeft>
            <ActionBadge hasCount={item.count > 0}>
              <BadgeCount hasCount={item.count > 0}>
                {item.count}
              </BadgeCount>
              <ActionArrow>
                <RightOutlined className="action-arrow" />
              </ActionArrow>
            </ActionBadge>
          </ActionItem>
        ))}
      </ActionItemsGrid>
    </ActionItemsContainer>
  );
};

export default ActionItems;

